import { useState, useMemo } from 'react'
import { useTenantConfig } from '@webmono/storefront-ui/providers'
import { cn } from '@webmono/storefront-ui/utils'

interface SchedulePickerProps {
  value?: string
  onChange: (datetime: string | undefined) => void
  leadTimeMinutes?: number
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
}

function formatDate(date: Date): string {
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)

  if (date.toDateString() === today.toDateString()) {
    return 'Today'
  }
  if (date.toDateString() === tomorrow.toDateString()) {
    return 'Tomorrow'
  }
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function generateTimeSlots(date: Date, openTime: string, closeTime: string, intervalMinutes: number, leadTimeMinutes: number): Date[] {
  const slots: Date[] = []
  const now = new Date()

  const [openHour, openMinute] = openTime.split(':').map(Number)
  const [closeHour, closeMinute] = closeTime.split(':').map(Number)

  const start = new Date(date)
  start.setHours(openHour, openMinute, 0, 0)

  const end = new Date(date)
  end.setHours(closeHour, closeMinute, 0, 0)

  const minTime = new Date(now.getTime() + leadTimeMinutes * 60 * 1000)

  let current = new Date(start)
  while (current <= end) {
    if (current > minTime) {
      slots.push(new Date(current))
    }
    current = new Date(current.getTime() + intervalMinutes * 60 * 1000)
  }

  return slots
}

function generateDates(daysAhead: number): Date[] {
  const dates: Date[] = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  for (let i = 0; i < daysAhead; i++) {
    const date = new Date(today)
    date.setDate(date.getDate() + i)
    dates.push(date)
  }

  return dates
}

export function SchedulePicker({ value, onChange, leadTimeMinutes = 30 }: SchedulePickerProps) {
  const tenant = useTenantConfig()
  const [isASAP, setIsASAP] = useState(!value)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [showPicker, setShowPicker] = useState(false)

  const dates = useMemo(() => generateDates(7), [])

  const timeSlots = useMemo(() => {
    if (!selectedDate) return []
    const hours = tenant.operations.businessHours
    return generateTimeSlots(
      selectedDate,
      hours.open,
      hours.close,
      15,
      leadTimeMinutes
    )
  }, [selectedDate, tenant.operations.businessHours, leadTimeMinutes])

  const handleASAP = () => {
    setIsASAP(true)
    setSelectedDate(null)
    onChange(undefined)
    setShowPicker(false)
  }

  const handleSchedule = () => {
    setIsASAP(false)
    setShowPicker(true)
    if (!selectedDate) {
      setSelectedDate(dates[0])
    }
  }

  const handleTimeSelect = (slot: Date) => {
    onChange(slot.toISOString())
    setShowPicker(false)
  }

  const selectedDateTime = value ? new Date(value) : null

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={handleASAP}
          className={cn(
            'p-4 rounded-lg border-2 text-center transition-all',
            isASAP
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50'
          )}
        >
          <svg className="w-6 h-6 mx-auto mb-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="font-medium text-foreground">ASAP</span>
          <p className="text-xs text-muted-foreground mt-1">
            Ready in ~{leadTimeMinutes} min
          </p>
        </button>

        <button
          type="button"
          onClick={handleSchedule}
          className={cn(
            'p-4 rounded-lg border-2 text-center transition-all',
            !isASAP
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50'
          )}
        >
          <svg className="w-6 h-6 mx-auto mb-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="font-medium text-foreground">Schedule</span>
          {selectedDateTime ? (
            <p className="text-xs text-muted-foreground mt-1">
              {formatDate(selectedDateTime)}, {formatTime(selectedDateTime)}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">Pick a time</p>
          )}
        </button>
      </div>

      {showPicker && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-4">
          <div>
            <h4 className="text-sm font-medium text-foreground mb-2">Select Date</h4>
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1">
              {dates.map((date) => {
                const hasSlots = generateTimeSlots(
                  date,
                  tenant.operations.businessHours.open,
                  tenant.operations.businessHours.close,
                  15,
                  leadTimeMinutes
                ).length > 0

                return (
                  <button
                    key={date.toISOString()}
                    type="button"
                    onClick={() => setSelectedDate(date)}
                    disabled={!hasSlots}
                    className={cn(
                      'flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                      selectedDate?.toDateString() === date.toDateString()
                        ? 'bg-primary text-primary-foreground'
                        : hasSlots
                        ? 'bg-muted text-foreground hover:bg-muted/80'
                        : 'bg-muted/50 text-muted-foreground cursor-not-allowed'
                    )}
                  >
                    {formatDate(date)}
                  </button>
                )
              })}
            </div>
          </div>

          {selectedDate && timeSlots.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">Select Time</h4>
              <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                {timeSlots.map((slot) => (
                  <button
                    key={slot.toISOString()}
                    type="button"
                    onClick={() => handleTimeSelect(slot)}
                    className={cn(
                      'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      selectedDateTime?.toISOString() === slot.toISOString()
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground hover:bg-muted/80'
                    )}
                  >
                    {formatTime(slot)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {selectedDate && timeSlots.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No available times for this date
            </p>
          )}
        </div>
      )}
    </div>
  )
}
